import { useNavigate, useParams } from 'react-router-dom';
import {
  useEditCampaignMutation,
  useGetCampaignDictionariesQuery,
  useGetCampaignQuery,
} from 'src/api/campaigns/hooks';
import { validateEmpty } from 'src/helpers/validators';
import { links } from 'src/router/routes';

export type FormData = {
  name: string;
  date_from: string;
  date_to: string;
  campaign_type: string;
  campaign_goal: string;
  location_type: string;
};

const defaultValues: Partial<FormData> = {
  name: '',
  date_from: '',
  date_to: '',
  campaign_type: '',
  campaign_goal: '',
  location_type: '',
};

const validate = {
  name: (value: string) => validateEmpty(value, 'Please enter campaign name'),
  date_from: (value: string) =>
    validateEmpty(value, 'Please select a valid start date'),
  date_to: (value: string) =>
    validateEmpty(value, 'Please select a valid end date'),
  campaign_type: (value: string) =>
    validateEmpty(value, 'Please select campaign type'),
  campaign_goal: (value: string) =>
    validateEmpty(value, 'Please select campaign goal'),
  location_type: (value: string) =>
    validateEmpty(value, 'Please select location type'),
};

export const useEditCampaign = () => {
  const { id } = useParams<{ id: string }>();
  const campaignId = id ? parseInt(id) : 0;

  const { editCampaignMutation, isPending } = useEditCampaignMutation();
  const { data: dictionaries, isLoading: isDictionariesLoading } =
    useGetCampaignDictionariesQuery();
  const { data: campaign, isLoading: isCampaignLoading } =
    useGetCampaignQuery(campaignId);

  const navigate = useNavigate();

  const initialValues: Partial<FormData> = campaign
    ? {
        name: campaign.name,
        date_from: campaign.date_from,
        date_to: campaign.date_to,
        campaign_type: campaign.campaign_type.toString(),
        campaign_goal: campaign.campaign_goal.toString(),
        location_type: campaign.location_type.toString(),
      }
    : defaultValues;

  const onSubmit = async (data: FormData) => {
    if (!campaignId || !data.date_from || !data.date_to) {
      return;
    }

    const dateFrom = new Date(data.date_from).toISOString();
    const dateTo = new Date(data.date_to).toISOString();

    await editCampaignMutation({
      id: campaignId,
      data: {
        name: data.name,
        date_from: dateFrom,
        date_to: dateTo,
        campaign_type: parseInt(data.campaign_type),
        campaign_goal: parseInt(data.campaign_goal),
        location_type: parseInt(data.location_type),
      },
    });

    navigate(`${links.campaign}/${campaignId}`);
  };

  const onCancel = () => {
    navigate(`${links.campaign}/${campaignId}`);
  };

  const campaignTypeOptions =
    dictionaries?.campaign_types.map((item) => ({
      label: item.name,
      value: item.id.toString(),
    })) || [];

  const campaignGoalOptions =
    dictionaries?.campaign_goals.map((item) => ({
      label: item.name,
      value: item.id.toString(),
    })) || [];

  const locationTypeOptions =
    dictionaries?.location_types.map((item) => ({
      label: item.name,
      value: item.id.toString(),
    })) || [];

  return {
    isPending: isPending || isDictionariesLoading || isCampaignLoading,
    onSubmit,
    onCancel,
    initialValues,
    validate,
    campaignTypeOptions,
    campaignGoalOptions,
    locationTypeOptions,
  };
};
